/**
 * Layout principal da aplicação
 * 
 * @author Equipe VISION DESK
 * @date 25/10/2025
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2,
  FolderOpen, 
  ClipboardList, 
  DollarSign,
  Clock, 
  FileText, 
  Users,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  BarChart3,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';
import { useMenuPermissions } from '../hooks/useMenuPermissions';
import LoadingSpinner from './LoadingSpinner';
import NotificationCenter from './NotificationCenter';
import FloatingActionButton from './FloatingActionButton';
import Breadcrumbs from './Breadcrumbs';
import { useInactivityAlert } from '../hooks/useInactivityAlert';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  // console.log('🔍 DEBUG: Layout renderizado', { children: !!children });
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { user, logout } = useAuth();
  const location = useLocation();
  const { getAccessibleMenus, loading: permissionsLoading } = useMenuPermissions();
  
  // console.log('🔍 DEBUG: Layout state', { 
  // user: !!user, 
  // permissionsLoading, 
  // location: location.pathname,
  // children: !!children 
  // });
  
  // Monitorar sessões inativas
  useInactivityAlert({
    enabled: !!user,
    inactivityThreshold: 30, // 30 minutos
  });

  // Mapear ícones por nome
  const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
    LayoutDashboard,
    Building2,
    FolderOpen,
    ClipboardList,
    DollarSign,
    Clock,
    FileText,
    Users,
    Settings,
    Shield,
    BarChart3,
    CreditCard,
    CheckCircle
  };

  // Obter menus acessíveis baseado nas permissões
  const accessibleMenus = getAccessibleMenus();
  
  // console.log('🔍 DEBUG: Layout - accessibleMenus', {
  // count: accessibleMenus.length,
  // menus: accessibleMenus.map(m => ({ key: m.menuKey, name: m.menuName, canAccess: m.canAccess }))
  // });
  
  // Função para agrupar menus de administração
  const groupMenus = (menus: any[]) => {
    const groupedMenus = [];
    const administrationMenus = [];
    
    for (const menu of menus) {
      if (menu.menuKey === 'administration' || 
          menu.menuKey === 'admin-spreadsheet' ||
          menu.menuKey === 'permissions' || 
          menu.menuKey === 'tenants' || 
          menu.menuKey === 'company-areas' || 
          menu.menuKey === 'hour-rates' ||
          menu.menuKey === 'users' ||
          menu.menuKey === 'sla' ||
          menu.menuKey === 'upgrade-renew') {
        // Garantir href correto para upgrade-renew
        let href = menu.menuHref;
        if (menu.menuKey === 'upgrade-renew' && (!href || href === '/dashboard' || href !== '/administration/upgrade-renew')) {
          href = '/administration/upgrade-renew';
        }
        
        administrationMenus.push({
          name: menu.menuName,
          href: href,
          icon: iconMap[menu.menuIcon || 'LayoutDashboard'] || LayoutDashboard,
          menuKey: menu.menuKey,
          permissions: {
            canCreate: menu.canCreate,
            canEdit: menu.canEdit,
            canDelete: menu.canDelete,
            canView: menu.canView
          }
        });
      } else {
        groupedMenus.push({
          name: menu.menuName,
          href: menu.menuHref,
          icon: iconMap[menu.menuIcon || 'LayoutDashboard'] || LayoutDashboard,
          menuKey: menu.menuKey,
          permissions: {
            canCreate: menu.canCreate,
            canEdit: menu.canEdit,
            canDelete: menu.canDelete,
            canView: menu.canView
          }
        });
      }
    }
    
    // Se há menus de administração, criar grupo
    if (administrationMenus.length > 0) {
      const administrationMenu = administrationMenus.find(m => m.menuKey === 'administration');
      const subMenus = administrationMenus.filter(m => m.menuKey !== 'administration');
      
      if (administrationMenu && subMenus.length > 0) {
        const groupedMenu = {
          ...administrationMenu,
          subMenus: subMenus
        };
        groupedMenus.push(groupedMenu);
      } else if (administrationMenu) {
        // Se há menu principal mas não há submenus, adicionar apenas o menu principal
        groupedMenus.push(administrationMenu);
      } else {
        // Se não há menu principal de administração, adicionar os submenus diretamente
        groupedMenus.push(...subMenus);
      }
    }
    
    return groupedMenus;
  };
  
  // Converter permissões para formato de navegação com agrupamento
  const navigation = useMemo(() => groupMenus(accessibleMenus), [accessibleMenus]);

  useEffect(() => {
    const activeMenu = navigation.find(
      (item) => item.subMenus && item.subMenus.some((sub) => location.pathname === sub.href)
    );

    if (activeMenu && !expandedMenus.includes(activeMenu.menuKey)) {
      setExpandedMenus((prev) => [...prev, activeMenu.menuKey]);
    }
  }, [location.pathname, navigation, expandedMenus]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // console.error('Erro ao fazer logout:', error);
    }
  };

  const toggleMenuExpansion = (menuKey: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuKey) 
        ? prev.filter(key => key !== menuKey)
        : [...prev, menuKey]
    );
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Mostrar loading enquanto carrega as permissões
  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-background-dark">
      {/* Sidebar Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full sidebar">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-background-glass backdrop-blur-glass border border-border-glass text-text-primary hover:bg-background-glass-hover transition-all duration-300"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarContent 
              navigation={navigation} 
              location={location} 
              expandedMenus={expandedMenus}
              onToggleExpansion={toggleMenuExpansion}
              collapsed={false}
            />
          </div>
        </div>
      )}

      {/* Sidebar Desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div
          className={cn(
            'flex flex-col transition-all duration-300 border-r border-border-glass sidebar',
            sidebarCollapsed ? 'w-16' : 'w-56'
          )}
        >
          <SidebarContent 
            navigation={navigation} 
            location={location} 
            expandedMenus={expandedMenus}
            onToggleExpansion={toggleMenuExpansion}
            collapsed={sidebarCollapsed}
          />
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <div className="relative z-10 flex-shrink-0 flex h-12 navbar">
          <button
            type="button"
            className="px-4 border-r border-border-glass text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary md:hidden transition-colors duration-300"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-3 flex justify-between">
            <div className="flex-1 flex items-center space-x-2">
              <button
                type="button"
                className="hidden md:inline-flex h-8 w-8 items-center justify-center rounded-button border border-border-glass text-text-secondary hover:text-text-primary hover:border-secondary/60 transition-all duration-300"
                onClick={toggleSidebarCollapsed}
                aria-label={sidebarCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              >
                {sidebarCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </button>
              <h1 className="text-sm sm:text-lg font-semibold text-text-primary tracking-tight truncate">
                VISION DESK
              </h1>
            </div>
            
            {/* Menu do usuário */}
            <div className="ml-3 flex items-center md:ml-4 space-x-2">
              {/* Centro de Notificações */}
              <NotificationCenter />
              <div className="relative">
                <button
                  type="button"
                  className="max-w-xs bg-background-glass backdrop-blur-glass border border-border-glass flex items-center text-sm rounded-button focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary focus:ring-offset-background-dark hover:bg-background-glass-hover transition-all duration-300"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <div className="h-7 w-7 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <div className="text-xs font-medium text-text-primary">{user?.name}</div>
                      <div className="text-xs text-text-muted">{user?.role}</div>
                    </div>
                    <ChevronDown className="h-3 w-3 text-text-muted" />
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 dropdown-content focus:outline-none z-50">
                    <button
                      onClick={handleLogout}
                      className="dropdown-button"
                    >
                      <LogOut className="dropdown-icon" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-2 sm:py-4">
            <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 xl:px-8">
              {/* Breadcrumbs - Oculto em mobile muito pequeno */}
              <div className="mb-2 sm:mb-4 hidden xs:block">
                <Breadcrumbs />
              </div>
              {children}
            </div>
          </div>
        </main>
      </div>
      
      {/* Marca d'água VISION DESK */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img 
          src="/vision-desk-logo.png" 
          alt="VISION DESK Watermark" 
          className="w-[90vw] h-auto max-w-4xl opacity-5"
        />
      </div>

      {/* Floating Action Button (FAB) */}
      <FloatingActionButton />
    </div>
  );
}

interface SidebarContentProps {
  navigation: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    menuKey: string;
    subMenus?: Array<{
      name: string;
      href: string;
      icon: React.ComponentType<{ className?: string }>;
      menuKey: string;
    }>;
  }>;
  location: { pathname: string };
  expandedMenus: string[];
  onToggleExpansion: (menuKey: string) => void;
  collapsed: boolean;
}

function SidebarContent({ navigation, location, expandedMenus, onToggleExpansion, collapsed }: SidebarContentProps) {
  return (
    <div
      className={cn(
        'flex flex-col h-0 flex-1 bg-background-glass',
        collapsed ? 'items-center' : 'border-r border-border-glass sidebar'
      )}
    >
      <div className="flex-1 flex flex-col pt-3 pb-3 overflow-y-auto w-full">
        <nav
          className={cn(
            'mt-2 flex-1 space-y-0.5',
            collapsed ? 'px-1' : 'px-1.5'
          )}
        >
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.subMenus && item.subMenus.some((sub) => location.pathname === sub.href));
            const isExpanded = expandedMenus.includes(item.menuKey);
            const hasSubMenus = !!(item.subMenus && item.subMenus.length > 0);
            
            // Garantir que o ícone existe, usar LayoutDashboard como fallback
            const IconComponent = item.icon || LayoutDashboard;

            return (
              <div
                key={item.menuKey}
                className={cn('relative', collapsed ? 'flex justify-center' : '')}
              >
                <div className={cn('flex items-center', collapsed ? 'justify-center w-full' : '')}>
                  {hasSubMenus ? (
                    <button
                      onClick={() => onToggleExpansion(item.menuKey)}
                      className={cn(
                        'group flex items-center px-2 py-1.5 text-sm font-medium rounded-button flex-1 transition-all duration-300',
                        collapsed ? 'justify-center' : '',
                        isActive
                          ? 'bg-gradient-to-r from-primary/20 to-secondary/20 text-text-primary border border-primary/30 shadow-glow'
                          : 'text-text-secondary hover:bg-background-glass-hover hover:text-text-primary border border-transparent'
                      )}
                      title={collapsed ? item.name : undefined}
                      aria-label={collapsed ? item.name : undefined}
                    >
                      <IconComponent
                        className={cn(
                          'flex-shrink-0 h-4 w-4 transition-colors duration-300',
                          collapsed ? '' : 'mr-2',
                          isActive ? 'text-secondary' : 'text-text-muted group-hover:text-text-primary'
                        )}
                      />
                      <span
                        className={cn(
                          'transition-all duration-200',
                          collapsed ? 'sr-only' : 'truncate'
                        )}
                      >
                        {item.name}
                      </span>
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex items-center px-2 py-1.5 text-sm font-medium rounded-button flex-1 transition-all duration-300',
                        collapsed ? 'justify-center' : '',
                        isActive
                          ? 'bg-gradient-to-r from-primary/20 to-secondary/20 text-text-primary border border-primary/30 shadow-glow'
                          : 'text-text-secondary hover:bg-background-glass-hover hover:text-text-primary border border-transparent'
                      )}
                      title={collapsed ? item.name : undefined}
                      aria-label={collapsed ? item.name : undefined}
                    >
                      <IconComponent
                        className={cn(
                          'flex-shrink-0 h-4 w-4 transition-colors duration-300',
                          collapsed ? '' : 'mr-2',
                          isActive ? 'text-secondary' : 'text-text-muted group-hover:text-text-primary'
                        )}
                      />
                      <span
                        className={cn(
                          'transition-all duration-200',
                          collapsed ? 'sr-only' : 'truncate'
                        )}
                      >
                        {item.name}
                      </span>
                    </Link>
                  )}

                  {hasSubMenus && (
                    <button
                      onClick={() => onToggleExpansion(item.menuKey)}
                      className={cn(
                        'p-1 rounded-button hover:bg-background-glass-hover transition-colors duration-300 ml-0.5',
                        collapsed ? 'hidden md:inline-flex' : ''
                      )}
                      aria-label={isExpanded ? `Recolher ${item.name}` : `Expandir ${item.name}`}
                      title={isExpanded ? `Recolher ${item.name}` : `Expandir ${item.name}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3 text-text-muted hover:text-text-primary transition-colors duration-300" />
                      ) : (
                        <ChevronRight className="h-3 w-3 text-text-muted hover:text-text-primary transition-colors duration-300" />
                      )}
                    </button>
                  )}
                </div>

                {hasSubMenus && isExpanded && (
                  <div
                    className={cn(
                      'space-y-0.5 mt-1',
                      collapsed
                        ? 'absolute left-full top-0 ml-3 w-56 bg-background-glass border border-border-glass rounded-lg shadow-glass px-2 py-2 z-50'
                        : 'ml-3'
                    )}
                  >
                    {item.subMenus!.map((subItem) => {
                      const isSubActive = location.pathname === subItem.href;
                      const SubIconComponent = subItem.icon || LayoutDashboard;
                      return (
                        <Link
                          key={subItem.menuKey}
                          to={subItem.href}
                          className={cn(
                            'group flex items-center px-2 py-1 text-xs font-medium rounded-button transition-all duration-300',
                            isSubActive
                              ? 'bg-gradient-to-r from-accent/20 to-primary/20 text-text-primary border border-accent/30'
                              : 'text-text-muted hover:bg-background-glass-hover hover:text-text-primary border border-transparent'
                          )}
                          title={collapsed ? subItem.name : undefined}
                          aria-label={collapsed ? subItem.name : undefined}
                        >
                          <SubIconComponent
                            className={cn(
                              'mr-2 flex-shrink-0 h-3 w-3 transition-colors duration-300',
                              isSubActive ? 'text-accent' : 'text-text-muted group-hover:text-text-primary'
                            )}
                          />
                          <span className="truncate">{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
